//New Code by LM date : 12/01/2025
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchCategories,
  getAttribute,
  getAttributeOptions,
  getBrands,
  getItemsbyItemId,
  getManufacturer,
} from "../Services/inventoryService"; // Adjust path based on your folder structure
import { Item } from "./ViewInventory";
import Footer from "../Footer/Footer";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Container,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { LABELS, LBL_BACK, LBL_BATCH_NUMBER, LBL_COST_PRICE, LBL_DISCOUNT_RATE, LBL_EXPIRY_DATE, LBL_IAN, LBL_INVENTORY_AND_PRICING, LBL_LOT_NUMBER, LBL_NO_ADDITIONAL_ATTRIBUTES_AVAILABLE, LBL_QUANTITY, LBL_REORDER_POINT, LBL_SELLING_PRICE, LBL_SKU, LBL_UOM, LBL_UPC, LBL_WAREHOUSE } from "../Constants/LabelConstants";

const ViewInventoryDetails: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  // State to hold item details
  const [item, setItem] = useState<Item | null>(null); // To store single item
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState<
    { attributeId: string | number; name: string }[]
  >([]);
  const [attributeOptions, setAttributeOptions] = useState<{
    [key: string]: { id: string | number; name: string }[];
  }>({});
  const handleBackClick = () => {
    navigate("/dashboard/inventory/view-inventory");
  };
  const formatExpiryDate = (
    expiryDate: { _seconds: number; _nanoseconds: number } | string | undefined
  ) => {
    if (!expiryDate) return "";

    let date: Date;

    if (typeof expiryDate === "string") {
      // If it's an ISO date string
      date = new Date(expiryDate);
    } else if (typeof expiryDate === "object" && "_seconds" in expiryDate) {
      // If it's a Firestore Timestamp
      date = new Date(
        expiryDate._seconds * 1000 + expiryDate._nanoseconds / 1000000
      );
    } else {
      return "Invalid Date";
    }

    return date.toLocaleDateString(); // ✅ Returns only the date (based on locale)
  };

  // Fetch item details from the API
  const fetchItemDetails = async () => {
    try {
      const state = location.state as {
        item: { itemId: string; orgId: string };
      };
      const { orgId, itemId } = state.item;
      const fetchedItem = await getItemsbyItemId(orgId, itemId);
      //setItem(fetchedItem); // Set the fetched item to the state

      let categoryId = "N/A";
      let brandId = "N/A";
      let manufacturerId = "N/A";
      try {
        const categoryResponse = await fetchCategories(orgId);
        categoryId =
          categoryResponse?.find(
            (cat: { categoryId: string }) =>
              cat.categoryId === fetchedItem.categoryId
          )?.categoryName || "N/A";
      } catch (error) {
        console.warn("Category fetch failed:", error);
      }
      // Fetch Brand Name
      try {
        const brandResponse = await getBrands(orgId, fetchedItem.categoryId);
        brandId =
          brandResponse?.find(
            (brand: { brandId: string }) =>
              brand.brandId === fetchedItem.brandId
          )?.brandName || "N/A";
      } catch (error) {
        console.warn("Brand fetch failed:", error);
      }
      // Fetch Manufacturer Name
      try {
        const manufacturerResponse = await getManufacturer(
          orgId,
          fetchedItem.brandId
        );
        manufacturerId =
          manufacturerResponse?.find(
            (manu: { manufacturerId: string }) =>
              manu.manufacturerId === fetchedItem.manufacturerId
          )?.manufacturerName || "N/A";
      } catch (error) {
        console.warn("Manufacturer fetch failed:", error);
      }
      // Update State with Names
      setItem({
        ...fetchedItem,
        categoryId,
        brandId,
        manufacturerId,
      });
    } catch (error) {
      console.error("Error fetching item details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemDetails();
  }, []);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const attributeList = await getAttribute();
        setAttributes(attributeList);

        // Fetch options for each attribute ID
        const optionsMap: {
          [key: string]: { id: string | number; name: string }[];
        } = {};
        for (const attr of attributeList) {
          const options = await getAttributeOptions(attr.attributeId);
          optionsMap[attr.attributeId] = options;
        }
        setAttributeOptions(optionsMap);
      } catch (error) {
        console.error("Error fetching attribute data:", error);
      }
    };

    fetchAttributes();
  }, []);

  if (loading) return <p>Loading...</p>;

  if (!item) return <p>No item details available.</p>;

  return (
    <>
      <div className="page-location">
        <span>Inventory &gt; View Inventory &gt; View Item Details </span>
      </div>
      <Container maxWidth="lg" sx={{ padding: 1 }}>
        <button onClick={handleBackClick} className="back-button">
          <i className="fas fa-arrow-left back-icon"></i>
          {LBL_BACK}
        </button>

        <Box
          sx={{
            marginTop: 3,
            padding: 3,
            backgroundColor: "#f4f6f8",
            borderRadius: "12px",
            boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Grid container spacing={4}>
            {/* Combined Item Details and Pricing */}
            <Grid item xs={12}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: "16px",
                  padding: "24px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 2,
                    color: "#34495e",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    textAlign: "center",
                  }}
                >
                  {LBL_INVENTORY_AND_PRICING}
                </Typography>
                <Divider sx={{ marginBottom: 2 }} />

                <Grid container spacing={4}>
                  {/* Item Details Table */}
                  <Grid item xs={12} md={6}>
                    <Table>
                      <TableBody>
                        {[
                          { label:  LABELS.ITEM_ID, value: item.itemId },
                          { label: LABELS.ITEM_NAME, value: item.name },
                          { label: LABELS.BRAND, value: item.brandId },
                          { label: LABELS.CATEGORY, value: item.categoryId },
                          { label: LABELS.MANUFACTURER, value: item.manufacturerId },
                          { label: LABELS.HSN, value: item.hsn },
                          {
                            label: LABELS.TAXABLE,
                            value: item.taxable ? "Yes" : "No",
                          },
                          { label: LABELS.TYPE, value: item.type },
                          { label: LABELS.UNIT, value: "units" },
                          { label: LABELS.ITEM_TYPE, value: item.itemType },
                          { label: LABELS.DESCRIPTION, value: item.desc },
                          // {
                          //   label: "Status",
                          //   value: item.createAttributes ? "Active" : "Inactive",
                          // },
                        ].map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{ borderBottom: "1px solid #ddd" }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2c3e50",
                                padding: "8px 16px",
                              }}
                            >
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#7f8c8d",
                                padding: "8px 16px",
                              }}
                            >
                              {row.value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>

                  {/* Pricing & Inventory Table */}
                  <Grid item xs={12} md={6}>
                    <Table>
                      <TableBody>
                        {[
                          {
                            label: LBL_SELLING_PRICE,
                            value: `₹${item.sellingPrice}`,
                          },
                          { label: LBL_COST_PRICE, value: `₹${item.costPrice}` },
                          { label: LBL_REORDER_POINT, value: item.reorderPoint },
                          { label: LBL_SKU, value: item.sku },
                          { label: LBL_UPC, value: item.upc },
                          { label: LBL_IAN, value: item.ian },
                          { label: LBL_QUANTITY, value: item.quantity },
                          { label: LBL_LOT_NUMBER, value: item.lotNumber },
                          { label: LBL_DISCOUNT_RATE, value: item.discountRate },
                          { label: LBL_BATCH_NUMBER, value: item.batchNumber },
                          { label: LBL_UOM, value: item.uom },
                          { label: LBL_WAREHOUSE, value: item.warehouse },
                          {
                            label: LBL_EXPIRY_DATE,
                            value: formatExpiryDate(item.expiryDate),
                          },
                        ].map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{ borderBottom: "1px solid #ddd" }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2c3e50",
                                padding: "8px 16px",
                              }}
                            >
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#7f8c8d",
                                padding: "8px 16px",
                              }}
                            >
                              {row.value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Item Attributes */}
            <Grid item xs={12}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: "16px",
                  padding: "24px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 2,
                    color: "#34495e",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  {LABELS.ITEM_ATTRIBUTES}
                </Typography>
                <Divider sx={{ marginBottom: 2 }} />
                {/* <Table>
                  <TableBody>
                    {item.attributes && item.attributes.length > 0 ? (
                      item.attributes.map((attribute, index) => (
                        <TableRow key={index} sx={{ borderBottom: "1px solid #ddd" }}>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: "#2c3e50",
                              padding: "8px 16px",
                            }}
                          >
                            {attribute.attribute}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#7f8c8d",
                              padding: "8px 16px",
                            }}
                          >
                            {attribute.option}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            textAlign: "center",
                            padding: "16px",
                            color: "#7f8c8d",
                          }}
                        >
                          No additional attributes available.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table> */}
                <Table>
                  <TableBody>
                    {item.attributes && item.attributes.length > 0 ? (
                      item.attributes.map((attribute, index) => {
                        // Find attribute name by attribute ID
                        const attributeName =
                          attributes.find(
                            (attr) => attr.attributeId == attribute.attribute
                          )?.name || "Unknown Attribute";

                        // Find attribute option name by option ID
                        const attributeValue =
                          attributeOptions[attribute.attribute]?.find(
                            (opt) => opt.id == attribute.option
                          )?.name || "Unknown Option";

                        return (
                          <TableRow
                            key={index}
                            sx={{ borderBottom: "1px solid #ddd" }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2c3e50",
                                padding: "8px 16px",
                              }}
                            >
                              {attributeName}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#7f8c8d",
                                padding: "8px 16px",
                              }}
                            >
                              {attributeValue}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            textAlign: "center",
                            padding: "16px",
                            color: "#7f8c8d",
                          }}
                        >
                          {LBL_NO_ADDITIONAL_ATTRIBUTES_AVAILABLE}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <Box
        sx={{
          position: "relative",
          left: 0,
          bottom: 0,
          width: "100%",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

export default ViewInventoryDetails;
